import { SvgIconComponent } from '@mui/icons-material';
import AltRoute from '@mui/icons-material/AltRoute';
import Anchor from '@mui/icons-material/Anchor';
import CreateOutlined from '@mui/icons-material/CreateOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import DirectionsBoatSharp from '@mui/icons-material/DirectionsBoatSharp';
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined';
import FmdGoodOutlined from '@mui/icons-material/FmdGoodOutlined';
import Fullscreen from '@mui/icons-material/Fullscreen';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import History from '@mui/icons-material/History';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MapOutlined from '@mui/icons-material/MapOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum EMenuItems {
  NOTHING_SELECTED = '',
  CITIES = 'Cities',
  COUNTRIES = 'Countries',
  MAPS = 'Maps / Charts',
  FLEET = 'Fleet',
  ALERTS = 'Fleet Notifications',
  INCIDENTS = 'Incidents',
  BOUNDARIES = 'Boundaries',
  PORTS = 'Ports',
  ROUTES = 'Routes',
  DRAWINGS = 'Drawings',
  RI_MARITIME_AREAS = 'Risk Intelligence Maritime Areas',
  HISTORY = 'History',
  DOCUMENTS = 'Documents',
  SAVE_SETUP = 'Save Setup',
  DATA_POLICY = 'Data Policy',
  FEEDBACK = 'Feedback & Support',
  HELP_PAGES = 'Help Pages',
}

export enum EDossiers {
  NOTHING = '',
  CITY = 'City',
  COUNTRY = 'Country',
  INCIDENT = 'Incident',
  PORT = 'Port',
  DRAWING = 'Drawing',
  RI_MARITIME_AREA = 'RI Maritime Area',
  ROUTE = 'Route',
  VESSEL = 'Vessel',
}

export enum EMenuIcons {
  MAPS = 'MapOutlined',
  FLEET = 'DirectionsBoatOutlined',
  FLEETS = 'DirectionsBoatSharp',
  ALERTS = 'NotificationsOutlined',
  INCIDENTS = 'FmdGoodOutlined',
  BOUNDARIES = 'Fullscreen',
  PORTS = 'Anchor',
  ROUTES = 'AltRoute',
  DRAWINGS = 'CreateOutlined',
  RI_MARITIME_AREAS = 'GroupOutlined',
  HISTORY = 'History',
  DOCUMENTS = 'DescriptionOutlined',
  SAVE_SETUP = 'SaveOutlined',
  DATA_POLICY = 'LockOutlined',
  FEEDBACK = 'FeedbackOutlined',
  HELP_PAGES = 'HelpOutlineOutlined',
}

export const MenuIcons: Record<EMenuIcons, SvgIconComponent> = {
  MapOutlined,
  DirectionsBoatOutlined,
  DirectionsBoatSharp,
  NotificationsOutlined,
  FmdGoodOutlined,
  Fullscreen,
  Anchor,
  AltRoute,
  CreateOutlined,
  GroupOutlined,
  History,
  SaveOutlined,
  LockOutlined,
  FeedbackOutlined,
  HelpOutlineOutlined,
  DescriptionOutlined,
};

export const EMenuTitles = {
  ALERTS_AND_INCIDENTS: 'Notifications & Incidents',
  MY_FLEET: 'My Fleet',
  AREAS: 'Areas',
  OBSERVATIONS: 'Observations',
  MAP_AND_TOOLS: 'Maps Options',
  USER: 'User',
};

export interface MenuState {
  menuOpen: boolean;
  selectedOption: EMenuItems;
  secondaryMenuOpen: boolean;
  selectedDossier: EDossiers;
  dossierHistory: { dossier: EDossiers; option: EMenuItems }[];
  securityRiskAssessmentOpen: boolean;
  threatAssessmentOpen: boolean;
  vulnerabilityAssessmentOpen: boolean;
  selectedAreaDocumentId: string;
}

export const INITIAL_MENU_STATE: MenuState = {
  menuOpen: false,
  selectedOption: EMenuItems.NOTHING_SELECTED,
  dossierHistory: [],
  secondaryMenuOpen: false,
  selectedDossier: EDossiers.NOTHING,
  securityRiskAssessmentOpen: false,
  threatAssessmentOpen: false,
  vulnerabilityAssessmentOpen: false,
  selectedAreaDocumentId: '',
};

const MenuSlice = createSlice({
  name: 'menu',
  initialState: INITIAL_MENU_STATE,
  reducers: {
    toggleMainMenu: (state) => {
      state.menuOpen = !state.menuOpen;
    },
    closeMainMenu: (state) => {
      state.menuOpen = false;
    },
    closeMenus: (state) => {
      state.menuOpen = false;
      state.secondaryMenuOpen = false;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    toggleSecondaryMenu: (state, action) => {
      if (action.payload) {
        state.secondaryMenuOpen = action.payload;
        state.menuOpen = false;
      } else {
        state.secondaryMenuOpen = action.payload;
      }
    },
    setSecondaryMenu: (state) => {
      state.secondaryMenuOpen = !state.secondaryMenuOpen;
    },
    setAllDossierClosed: (state) => {
      state.selectedDossier = EDossiers.NOTHING;
      state.dossierHistory = [];
      state.threatAssessmentOpen = false;
      state.securityRiskAssessmentOpen = false;
      state.vulnerabilityAssessmentOpen = false;
    },
    setAllAssessmentsClosed: (state) => {
      state.threatAssessmentOpen = false;
      state.securityRiskAssessmentOpen = false;
      state.vulnerabilityAssessmentOpen = false;
    },
    setSelectedOption: (state, action: PayloadAction<EMenuItems>) => {
      MenuSlice.caseReducers.setAllAssessmentsClosed(state);
      state.secondaryMenuOpen = true;
      state.selectedOption = action.payload;
    },
    setSelectedDossier: (state, action: PayloadAction<EDossiers>) => {
      if (state.selectedDossier === action.payload) {
        // if we're already on this dossier don't do anything
        return;
      }
      state.dossierHistory = [
        ...state.dossierHistory,
        { dossier: state.selectedDossier, option: state.selectedOption },
      ];
      state.selectedDossier = action.payload;
    },
    setSelectedAreaDocument: (state, action: PayloadAction<string>) => {
      state.selectedAreaDocumentId = action.payload;
    },
    setThreatAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.threatAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    setSecurityRiskAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.securityRiskAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
    setVulnerabilityAssessmentOpen: (state, action: PayloadAction<boolean>) => {
      MenuSlice.caseReducers.setAllDossierClosed(state);
      state.vulnerabilityAssessmentOpen = action.payload;
      state.selectedOption = EMenuItems.NOTHING_SELECTED;
    },
  },
});

export const {
  toggleMainMenu,
  closeMenus,
  closeMainMenu,
  toggleSecondaryMenu,
  setSecondaryMenu,
  setThreatAssessmentOpen,
  setSecurityRiskAssessmentOpen,
  setVulnerabilityAssessmentOpen,
  setSelectedOption,
  setSelectedDossier,
  setAllDossierClosed,
  setAllAssessmentsClosed,
  setSelectedAreaDocument,
} = MenuSlice.actions;

export default MenuSlice.reducer;
